<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
        btnTitle="Add New"
        :showBtn="false"
        title="Reward Point Register"
      />
    </div>

    <div class="my-2 px-2">
      <DateQuerySetter
        btnTitle="Go"
        @onClickGo="onClickGo"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">
            Customer
          </label>
          <AsyncSelect
              placeholder="Party Name, ID, Mobile No"
              :api-service="fetchContactProfiles"
              :reduce="(name) => name.id"
              v-model="contactProfileId"
              :additional-query="{type: 'customer'}"
              :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div> 

    <div class="col-12 px-2 mt-2">
      <div class="d-flex justify-content-end mb-1">
        <button
            @click="goToPrint"
            class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1"
        >
          Print
        </button>
        <button
            @click="exportTable"
            class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
        >
          Export
        </button>
      </div>
      <RegisterListTable
        :tableItems="registerData"
        :totals="totals"
        :isLastPage="isLastPage"
       />
    </div>

    <div class="px-2">
      <Pagination 
        :offset="offset" 
        @onPageChange="onPageChange" 
        ref="pagination"
      />
    </div>
  </div>
</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import {ref, inject, computed, onMounted} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import TitleButton from '@/components/atom/TitleButton';
import Pagination from '@/components/atom/Pagination';
import RegisterListTable from '@/components/atom/super-shop/reward-point/RegisterListTable.vue';
import handleInventory from "@/services/modules/inventory";
import handleContact from "@/services/modules/contact";
import handleCompany from "@/services/modules/company";
import handleBarcode from "@/services/modules/barcode";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import rewardPointRegisterPdf from '@/services/utils/pdf/rewardPointRegister';

const router = useRouter()
const store = useStore()
const route = useRoute()
const { fetchRewardRegister } = handleInventory();
const { fetchContactProfiles } = handleContact();
const { fetchCompanyInfo } = handleCompany();
const { fetchBarcode } = handleBarcode();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { exportToPDF } = rewardPointRegisterPdf();

let offset = ref(20)
const registerData = ref([]);
const totals = ref({});
let contactProfileId = ref(null)
const companyInfo = ref(null);

const showError = inject('showError');

const companyId = computed(() => {
  return route.params.companyId
})
const startDate = computed(() => {
  return route.query.start
})
const endDate = computed(() => {
  return route.query.end
})
const page = computed(() => {
  return route.query.page || 1
})

const token = computed(() => localStorage.getItem('token'));

const isLastPage = computed(() => {
  const { records, perPage, currentPage } = store.state.paginationData;
  if (!perPage) return true;
  return currentPage >= Math.ceil(records / perPage);
});

const getQuery = () => {
  let query = '?company_id=' + companyId.value + '&status=approved';
  if(startDate.value) query += '&start_date='+startDate.value;
  if(endDate.value) query += '&end_date='+endDate.value;
  if(contactProfileId.value) query += '&contact_profile_id='+contactProfileId.value;
  query += '&offset='+ offset.value;
  query += '&page='+ page.value;
  return query
}

const onClickGo = async() => {
  registerData.value = [];
  totals.value = {};

  try{
    const res = await fetchRewardRegister(getQuery());
    if(! res.status) {
      store.commit("resetPagination")
      return;
    }
    registerData.value = res.data.data;
    totals.value = res.totals;
    setPagination(res.data);
  } catch(err){
    showError(err)
  }
};

const goToPrint = async () => {
  const userName = store.state.user.user.name;
  try {
    const res = await fetchRewardRegister(getQuery() + '&is_all=1');
    if(!res.status) return;
    const qrcodeData = JSON.stringify({
      created_by: userName,
      created_date_time: new Date().toISOString()
    });
    const barcodeRes = await fetchBarcode(`?code=reward-point-register&qrcode=${qrcodeData}`);
    exportToPDF(companyInfo.value, res.data, res.totals, userName, barcodeRes.qrcode);
  } catch(err) {
    showError(err);
  }
};

const exportTable = () => {
  let query = getQuery();
  let url = `${process.env.VUE_APP_BASE_URL}/api/inventory/reward-points/register${query}&_token=${token.value}&is_all=1&is_export=1`;
  let a = document.createElement("a");
  a.setAttribute("href", url);
  a.click();
}

function setPagination(data) {
  if(!data.data.length) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await onClickGo()
};

const getCompany = async () => {
  const res = await fetchCompanyInfo(companyId.value);
  if(res.data) companyInfo.value = res.data;
}

onMounted(() => {
  onClickGo();
  getCompany();
})
</script>
